import React, { useEffect, useState } from "react";
import { Row, Col, CardBody, Card, Alert, Container, Input, Label, Form, FormFeedback, Button } from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import { Link, useNavigate } from "react-router-dom";
import toastr from "toastr";
import "toastr/build/toastr.css";
import { registerUser, apiError } from "../../store/actions";
import { getCompanyName } from "constants/constAPI";

toastr.options = {
  closeButton: true,
  progressBar: true,
  positionClass: "toast-top-right",
  timeOut: 4000,
  extendedTimeOut: 1000,
  preventDuplicates: true,
};

const Register = () => {
  document.title = "Register New Firm | 7-Day Free Trial";

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showPass, setShowPass] = useState(false);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      firmName: "",
      ownerName: "",
      mobileNo: "",
      email: "",
      city: "",
      state: "",
      gstNo: "",
      password: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      firmName: Yup.string()
        .min(3, "Firm name must be at least 3 characters")
        .required("Please enter Firm Name"),
      ownerName: Yup.string()
        .min(2, "Owner name must be at least 2 characters")
        .required("Please enter Owner / Contact Person name"),
      mobileNo: Yup.string()
        .matches(/^[6-9]\d{9}$/, "Please enter a valid 10-digit Indian mobile number")
        .required("Mobile number is required (used as Login Username)"),
      email: Yup.string()
        .email("Please enter a valid email address")
        .required("Email address is required (needed for account recovery & OTP)"),
      city: Yup.string().required("Please enter City"),
      state: Yup.string().required("Please enter State"),
      password: Yup.string()
        .min(4, "Password must be at least 4 characters")
        .required("Please enter Password"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password"), null], "Passwords do not match")
        .required("Please confirm your Password"),
    }),
    onSubmit: (values) => {
      dispatch(registerUser({
        firmName: values.firmName.trim(),
        ownerName: values.ownerName.trim(),
        mobileNo: values.mobileNo.trim(),
        email: values.email.trim(),
        city: values.city.trim(),
        state: values.state.trim(),
        gstNo: values.gstNo.trim(),
        password: values.password,
        username: values.mobileNo.trim(),
      }));
    },
  });

  const AccountProperties = createSelector(
    (state) => state.Account,
    (account) => ({
      user: account.user,
      registrationError: account.registrationError,
      loading: account.loading,
      success: account.success,
    })
  );

  const { user, registrationError, loading, success } = useSelector(AccountProperties);

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (loading) return;

    const errors = await validation.validateForm();

    validation.setTouched({
      firmName: true,
      ownerName: true,
      mobileNo: true,
      email: true,
      city: true,
      state: true,
      password: true,
      confirmPassword: true,
    });

    const fieldOrder = [
      { name: "firmName", label: "Firm Name" },
      { name: "ownerName", label: "Owner / Contact Person" },
      { name: "mobileNo", label: "Mobile Number" },
      { name: "email", label: "Email Address" },
      { name: "city", label: "City" },
      { name: "state", label: "State" },
      { name: "password", label: "Password" },
      { name: "confirmPassword", label: "Confirm Password" },
    ];

    for (const item of fieldOrder) {
      if (errors[item.name]) {
        toastr.warning(errors[item.name], item.label);
        const inputEl = document.querySelector(`[name="${item.name}"]`);
        if (inputEl) {
          inputEl.focus();
        }
        return;
      }
    }

    validation.handleSubmit();
  };

  useEffect(() => {
    dispatch(apiError(""));
  }, [dispatch]);

  useEffect(() => {
    if (success) {
      toastr.success(
        "Congratulations! Your firm has been registered successfully. Redirecting to login...",
        "Registration Successful"
      );
      const timer = setTimeout(() => navigate("/login"), 3000);
      return () => clearTimeout(timer);
    }
  }, [success, navigate]);

  useEffect(() => {
    if (registrationError) {
      const msg =
        typeof registrationError === "string"
          ? registrationError
          : registrationError?.message || "Registration failed. Please check your details and try again.";
      toastr.error(msg, "Registration Failed");
    }
  }, [registrationError]);

  useEffect(() => {
    // Enable full scrolling and clear any restrictive lock classes
    document.body.classList.remove("no-overscroll");
    document.documentElement.classList.remove("no-overscroll");
    document.body.classList.add("allow-overscroll", "register-active");
    document.documentElement.classList.add("allow-overscroll", "register-active");

    document.body.style.setProperty("overflow-y", "auto", "important");
    document.documentElement.style.setProperty("overflow-y", "auto", "important");
    document.body.style.setProperty("height", "auto", "important");
    document.documentElement.style.setProperty("height", "auto", "important");
    document.body.style.setProperty("-webkit-overflow-scrolling", "touch", "important");
    document.documentElement.style.setProperty("-webkit-overflow-scrolling", "touch", "important");

    const rootEl = document.getElementById("root");
    if (rootEl) {
      rootEl.style.setProperty("height", "auto", "important");
      rootEl.style.setProperty("min-height", "100%", "important");
      rootEl.style.setProperty("overflow", "visible", "important");
    }

    return () => {
      document.body.classList.remove("allow-overscroll", "register-active");
      document.documentElement.classList.remove("allow-overscroll", "register-active");
      document.body.style.removeProperty("overflow-y");
      document.documentElement.style.removeProperty("overflow-y");
      document.body.style.removeProperty("height");
      document.documentElement.style.removeProperty("height");
      document.body.style.removeProperty("-webkit-overflow-scrolling");
      document.documentElement.style.removeProperty("-webkit-overflow-scrolling");
    };
  }, []);
  return (
    <React.Fragment>
      <style>{`
        html.register-active,
        body.register-active {
          overflow-y: auto !important;
          overflow-x: hidden !important;
          height: auto !important;
          min-height: 100% !important;
          overscroll-behavior-y: auto !important;
          -webkit-overflow-scrolling: touch !important;
          touch-action: pan-y !important;
        }
        #root {
          min-height: 100% !important;
          height: auto !important;
        }
        @media (max-width: 767.98px) {
          .register-wrapper {
            padding: 12px 8px 36px !important;
          }
          .register-card {
            border-radius: 12px !important;
            margin: 0 !important;
          }
          .register-card-body {
            padding: 16px 14px !important;
          }
          .register-input {
            font-size: 15px !important;
            padding: 10px 12px !important;
            height: 42px !important;
          }
          .register-input-group .input-group-text {
            font-size: 14px !important;
            padding: 0 10px !important;
          }
          .register-header-badge {
            align-self: flex-start !important;
            margin-top: 4px !important;
          }
          .register-btn {
            padding: 13px !important;
            font-size: 15px !important;
          }
        }
      `}</style>
      <div
        className="account-pages register-wrapper"
        style={{
          background: "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)",
          minHeight: "100vh",
          minHeight: "100dvh",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          padding: "24px 12px 48px",
          position: "relative",
          overflowY: "auto",
          overflowX: "hidden",
          WebkitOverflowScrolling: "touch",
          touchAction: "pan-y",
        }}
      >
        <Container fluid style={{ maxWidth: "1040px", width: "100%", padding: "0 6px" }}>
          <Row className="justify-content-center m-0 w-100">
            <Col xs={12} className="p-0">
              <Card
                className="border-0 shadow-lg register-card"
                style={{
                  borderRadius: "16px",
                  backgroundColor: "#ffffff",
                  overflow: "hidden",
                  width: "100%",
                }}
              >
                {/* Header Bar */}
                <div
                  className="px-3 px-md-4 py-3 text-white d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-2"
                  style={{
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  }}
                >
                  <div className="d-flex align-items-center gap-3">
                    <div>
                      <h5 className="text-white fw-bold mb-0">Register Your Firm</h5>
                      <small className="text-white-50">
                        Multi-Firm Agricultural Brokerage & Ledger Management
                      </small>
                    </div>
                  </div>

                  <span
                    className="badge bg-warning text-dark px-3 py-2 fw-semibold d-inline-flex align-items-center gap-1 register-header-badge"
                    style={{ borderRadius: "20px", fontSize: "12px" }}
                  >
                    <i className="fas fa-gift"></i>
                    Instant 7-Day Free Trial Included
                  </span>
                </div>

                <CardBody className="p-3 p-md-4 register-card-body">
                  {success && (
                    <Alert color="success" className="d-flex align-items-center gap-2 mb-2 py-2">
                      <i className="fas fa-check-circle font-size-18"></i>
                      <div className="font-size-13">
                        <strong>Congratulations!</strong> Your firm has been registered successfully and your 7-day free trial is now active. Redirecting to login...
                      </div>
                    </Alert>
                  )}

                  {registrationError && (
                    <Alert color="danger" className="d-flex align-items-center gap-2 mb-2 py-2">
                      <i className="fas fa-exclamation-triangle font-size-18"></i>
                      <div className="font-size-13">
                        <strong>Registration Error:</strong> {registrationError}
                      </div>
                    </Alert>
                  )}

                  <Form noValidate onSubmit={handleFormSubmit}>
                    {/* Row 1: Firm Name, Owner Name, Mobile Number */}
                    <Row className="g-3 mb-2">
                      <Col xs={12} md={4}>
                        <Label className="form-label fw-semibold text-dark mb-1 font-size-13 register-label">
                          Firm Name <span className="text-danger">*</span>
                        </Label>
                        <Input
                          name="firmName"
                          placeholder="e.g. Radhey Trading Co."
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.firmName}
                          invalid={validation.touched.firmName && !!validation.errors.firmName}
                          className="form-control font-size-13 register-input"
                          style={{ borderRadius: "8px", padding: "8px 12px" }}
                        />
                        {validation.touched.firmName && validation.errors.firmName && (
                          <FormFeedback className="font-size-11">{validation.errors.firmName}</FormFeedback>
                        )}
                      </Col>

                      <Col xs={12} md={4}>
                        <Label className="form-label fw-semibold text-dark mb-1 font-size-13 register-label">
                          Owner / Contact Person <span className="text-danger">*</span>
                        </Label>
                        <Input
                          name="ownerName"
                          placeholder="e.g. Ramesh Kumar"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.ownerName}
                          invalid={validation.touched.ownerName && !!validation.errors.ownerName}
                          className="form-control font-size-13 register-input"
                          style={{ borderRadius: "8px", padding: "8px 12px" }}
                        />
                        {validation.touched.ownerName && validation.errors.ownerName && (
                          <FormFeedback className="font-size-11">{validation.errors.ownerName}</FormFeedback>
                        )}
                      </Col>

                      <Col xs={12} md={4}>
                        <Label className="form-label fw-semibold text-dark mb-1 font-size-13 register-label">
                          Mobile Number (Login ID) <span className="text-danger">*</span>
                        </Label>
                        <div className="input-group register-input-group">
                          <span className="input-group-text bg-light font-size-12 px-2" style={{ borderRadius: "8px 0 0 8px" }}>+91</span>
                          <Input
                            name="mobileNo"
                            maxLength="10"
                            placeholder="10-digit Mobile No"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.mobileNo}
                            invalid={validation.touched.mobileNo && !!validation.errors.mobileNo}
                            className="form-control font-size-13 register-input"
                            style={{ borderRadius: "0 8px 8px 0", padding: "8px 12px" }}
                          />
                        </div>
                        {validation.touched.mobileNo && validation.errors.mobileNo && (
                          <div className="text-danger font-size-11 mt-1">{validation.errors.mobileNo}</div>
                        )}
                      </Col>
                    </Row>

                    {/* Row 2: Email, City, State */}
                    <Row className="g-3 mb-2">
                      <Col xs={12} md={4}>
                        <Label className="form-label fw-semibold text-dark mb-1 font-size-13 register-label">
                          Email Address <span className="text-danger">*</span>
                        </Label>
                        <Input
                          name="email"
                          type="email"
                          placeholder="firm@example.com"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.email}
                          invalid={validation.touched.email && !!validation.errors.email}
                          className="form-control font-size-13 register-input"
                          style={{ borderRadius: "8px", padding: "8px 12px" }}
                        />
                        {validation.touched.email && validation.errors.email && (
                          <FormFeedback className="font-size-11">{validation.errors.email}</FormFeedback>
                        )}
                      </Col>

                      <Col xs={12} md={4}>
                        <Label className="form-label fw-semibold text-dark mb-1 font-size-13 register-label">
                          City <span className="text-danger">*</span>
                        </Label>
                        <Input
                          name="city"
                          placeholder="e.g. Indore"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.city}
                          invalid={validation.touched.city && !!validation.errors.city}
                          className="form-control font-size-13 register-input"
                          style={{ borderRadius: "8px", padding: "8px 12px" }}
                        />
                        {validation.touched.city && validation.errors.city && (
                          <FormFeedback className="font-size-11">{validation.errors.city}</FormFeedback>
                        )}
                      </Col>

                      <Col xs={12} md={4}>
                        <Label className="form-label fw-semibold text-dark mb-1 font-size-13 register-label">
                          State <span className="text-danger">*</span>
                        </Label>
                        <Input
                          name="state"
                          placeholder="e.g. Madhya Pradesh"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.state}
                          invalid={validation.touched.state && !!validation.errors.state}
                          className="form-control font-size-13 register-input"
                          style={{ borderRadius: "8px", padding: "8px 12px" }}
                        />
                        {validation.touched.state && validation.errors.state && (
                          <FormFeedback className="font-size-11">{validation.errors.state}</FormFeedback>
                        )}
                      </Col>
                    </Row>

                    {/* Row 3: GSTIN, Password, Confirm Password */}
                    <Row className="g-3 mb-3">
                      <Col xs={12} md={4}>
                        <Label className="form-label fw-semibold text-dark mb-1 font-size-13 register-label">
                          GSTIN <span className="text-muted font-size-11">(Optional)</span>
                        </Label>
                        <Input
                          name="gstNo"
                          placeholder="22AAAAA0000A1Z5"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.gstNo}
                          className="form-control font-size-13 text-uppercase register-input"
                          style={{ borderRadius: "8px", padding: "8px 12px" }}
                        />
                      </Col>

                      <Col xs={12} md={4}>
                        <Label className="form-label fw-semibold text-dark mb-1 font-size-13 register-label">
                          Password <span className="text-danger">*</span>
                        </Label>
                        <div className="position-relative">
                          <Input
                            name="password"
                            type={showPass ? "text" : "password"}
                            placeholder="Min 4 characters"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.password}
                            invalid={validation.touched.password && !!validation.errors.password}
                            className="form-control font-size-13 register-input"
                            style={{ borderRadius: "8px", padding: "8px 36px 8px 12px" }}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPass(!showPass)}
                            className="btn btn-link position-absolute end-0 top-50 translate-middle-y text-muted p-1 me-2"
                            style={{ textDecoration: "none" }}
                          >
                            <i className={showPass ? "fas fa-eye-slash" : "fas fa-eye"}></i>
                          </button>
                        </div>
                        {validation.touched.password && validation.errors.password && (
                          <FormFeedback className="font-size-11">{validation.errors.password}</FormFeedback>
                        )}
                      </Col>

                      <Col xs={12} md={4}>
                        <Label className="form-label fw-semibold text-dark mb-1 font-size-13 register-label">
                          Confirm Password <span className="text-danger">*</span>
                        </Label>
                        <Input
                          name="confirmPassword"
                          type={showPass ? "text" : "password"}
                          placeholder="Re-enter password"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.confirmPassword}
                          invalid={validation.touched.confirmPassword && !!validation.errors.confirmPassword}
                          className="form-control font-size-13 register-input"
                          style={{ borderRadius: "8px", padding: "8px 12px" }}
                        />
                        {validation.touched.confirmPassword && validation.errors.confirmPassword && (
                          <FormFeedback className="font-size-11">{validation.errors.confirmPassword}</FormFeedback>
                        )}
                      </Col>
                    </Row>

                    {/* Submit Button */}
                    <div className="d-grid mb-3">
                      <Button
                        type="submit"
                        className="register-btn"
                        disabled={loading}
                        style={{
                          background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
                          border: "none",
                          borderRadius: "10px",
                          padding: "12px",
                          fontSize: "15px",
                          fontWeight: "600",
                          boxShadow: "0 3px 12px rgba(56, 239, 125, 0.35)",
                          cursor: loading ? "not-allowed" : "pointer",
                          opacity: loading ? 0.75 : 1,
                        }}
                      >
                        {loading ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            Activating Firm & Starting Trial...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-rocket me-2"></i>
                            Activate Firm & Start 7-Day Free Trial
                          </>
                        )}
                      </Button>
                    </div>

                    {/* Bottom Links */}
                    <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center gap-2 pt-3 border-top font-size-13 text-muted">
                      <div>
                        Already registered?{" "}
                        <Link to="/login" className="text-primary fw-bold">
                          Sign In Here
                        </Link>
                      </div>
                      <div>
                        Need Help? Call:{" "}
                        <a href="tel:+919999999999" className="text-primary fw-bold">
                          +91 99999-99999
                        </a>
                      </div>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>

        {/* Responsive Footer */}
        <div className="mt-3 text-center w-100 pb-2">
          <small className="text-white-50">
            © {new Date().getFullYear()} Welcome. All rights reserved.
          </small>
        </div>
      </div>
    </React.Fragment>
  );
};

export default Register;
